/**
 * NotificationOverlay — bottom-right floating column of transient
 * pop-ups summarising key game events as they arrive.
 *
 * Drives off the same `allEvents` accumulator the GameLogPanel reads.
 * Filters down to the events that actually warrant a player-facing
 * pop-up (card plays, ability triggers, AI thoughts, round advances)
 * and ignores the bookkeeping noise (counter adjusts, resource
 * adjusts, dice rolls, chat — the log handles those).
 *
 * Each notification auto-dismisses after 3.5s; click anywhere on
 * the toast to dismiss early. Stack rendered top-down so the newest
 * sits at the bottom.
 *
 * The component owns its own queue state — `useNotifications` watches
 * `allEvents` for new entries and pushes them. We index by `at`
 * timestamp + a monotonically incrementing counter so duplicate
 * timestamps don't collide.
 */

import { type ReactElement, useEffect, useState } from 'react';
import type { CardData, PlayerId } from '../types.js';

type Notification = {
  id: string;
  text: string;
  tone: 'amber' | 'rose' | 'violet' | 'cosmos';
  at: number;
};

type Props = {
  events: readonly unknown[];
  viewer: PlayerId;
  cards: ReadonlyMap<string, CardData>;
};

const TTL_MS = 3500;
const MAX_VISIBLE = 5;

type ActionEvent = {
  kind?: string;
  payload?: { actor?: PlayerId; action?: { kind?: string; [k: string]: unknown } };
  at?: number;
};

const cardName = (cards: ReadonlyMap<string, CardData>, id: unknown): string => {
  if (typeof id !== 'string') return String(id ?? '');
  return cards.get(id)?.name ?? id;
};

/**
 * Convert one TableActionApplied event into a notification, or null
 * if the event isn't worth a pop-up. Counter/resource adjusts are
 * deliberately filtered — they'd flood the screen and the pills
 * already animate when the tick happens.
 */
const eventToNotification = (
  raw: ActionEvent,
  viewer: PlayerId,
  cards: ReadonlyMap<string, CardData>,
  monotonic: number,
): Notification | null => {
  if (raw.kind !== 'TableActionApplied') return null;
  const action = raw.payload?.action;
  if (!action || typeof action.kind !== 'string') return null;
  const actor = raw.payload?.actor;
  const isMine = actor === viewer;
  const side = isMine ? 'You' : 'Opponent';
  const at = raw.at ?? Date.now();
  const id = `${at}-${monotonic}`;
  const tone: Notification['tone'] = isMine ? 'amber' : 'rose';

  switch (action.kind) {
    case 'MoveCard': {
      const cardId = action.cardId as string;
      const from = action.from as { kind?: string } | undefined;
      const to = action.to as { kind?: string; realm?: string } | undefined;
      if (from?.kind === 'hand' && to?.kind === 'realm') {
        return {
          id,
          text: `${side} played ${cardName(cards, cardId)} → ${to.realm ?? '?'}`,
          tone,
          at,
        };
      }
      return null;
    }
    case 'AdvanceRound':
      return { id, text: `Round advanced`, tone: 'cosmos', at };
    case 'PassTurn':
      return { id, text: `${side} passed initiative`, tone: 'cosmos', at };
    case 'ChatMessage': {
      const text = action.text as string;
      if (typeof text !== 'string') return null;
      // AI thoughts arrive as ChatMessage with 🤖 prefix (B3).
      if (text.startsWith('🤖 ')) {
        return { id, text: text.slice(2).trim(), tone: 'violet', at };
      }
      return null;
    }
    case 'MoveStone': {
      const realm = action.realm as string;
      const to = action.to as PlayerId | null;
      if (to === null) return { id, text: `${side} released ${realm}'s stone`, tone, at };
      return { id, text: `${side} seized ${realm}'s stone`, tone, at };
    }
    case 'DeclareVictory':
      return { id, text: `${side} declared victory!`, tone: 'amber', at };
    case 'ConcedeMatch':
      return { id, text: `${side} conceded`, tone: 'rose', at };
    default:
      return null;
  }
};

export function NotificationOverlay({ events, viewer, cards }: Props): ReactElement | null {
  const [queue, setQueue] = useState<readonly Notification[]>([]);
  const [seenCount, setSeenCount] = useState(0);

  useEffect(() => {
    if (events.length <= seenCount) return;
    const fresh: Notification[] = [];
    for (let i = seenCount; i < events.length; i += 1) {
      const e = events[i] as ActionEvent;
      const n = eventToNotification(e, viewer, cards, i);
      if (n) fresh.push(n);
    }
    if (fresh.length > 0) {
      setQueue((q) => [...q, ...fresh].slice(-MAX_VISIBLE));
    }
    setSeenCount(events.length);
  }, [events, viewer, cards, seenCount]);

  // Per-notification auto-dismiss timer.
  useEffect(() => {
    if (queue.length === 0) return;
    const head = queue[0];
    if (!head) return;
    const elapsed = Date.now() - head.at;
    const remaining = Math.max(0, TTL_MS - elapsed);
    const t = setTimeout(() => setQueue((q) => q.slice(1)), remaining);
    return () => clearTimeout(t);
  }, [queue]);

  if (queue.length === 0) return null;

  return (
    <div className="fixed right-3 bottom-40 z-30 flex w-72 flex-col gap-2">
      {queue.map((n) => {
        const tone =
          n.tone === 'amber'
            ? 'border-amber-600/70 bg-amber-900/85 text-amber-50'
            : n.tone === 'rose'
              ? 'border-rose-600/70 bg-rose-900/85 text-rose-50'
              : n.tone === 'violet'
                ? 'border-violet-600/70 bg-violet-900/85 text-violet-50'
                : 'border-cosmos-600/70 bg-cosmos-900/85 text-cosmos-50';
        return (
          // biome-ignore lint/a11y/useKeyWithClickEvents: transient toast, click-to-dismiss only.
          // biome-ignore lint/a11y/noStaticElementInteractions: same.
          <div
            key={n.id}
            onClick={() => setQueue((q) => q.filter((x) => x.id !== n.id))}
            className={`cursor-pointer rounded border-l-4 px-3 py-2 text-sm shadow-lg ring-1 ring-cosmos-700 transition hover:brightness-110 ${tone}`}
            title="Click to dismiss"
          >
            {n.text}
          </div>
        );
      })}
    </div>
  );
}
